import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import StyledDataGrid from '../../../components/StyledDataGrid';
import { fetchShipmentAliquots, fetchShipmentRecords } from '../../../utils/api';
import { formatDateTimeInAppTimeZone } from '../../../utils/appDateTime';

const ShipmentHistory = () => {
  const [page, setPage] = useState({ page: 0, pageSize: 25 });
  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [aliquots, setAliquots] = useState([]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchShipmentRecords({ page: page.page, size: page.pageSize })
      .then(result => {
        if (!active) return;
        setShipments(result?.content || []);
        setTotal(result?.totalElements || 0);
        setError('');
      })
      .catch(err => {
        if (!active) return;
        setShipments([]);
        setTotal(0);
        setError(err?.response?.status === 403 ? 'Admin access is required for shipment history.' : 'Could not load shipment history.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page]);

  const show = async (shipment) => {
    setSelected(shipment);
    setAliquots([]);
    try {
      setAliquots(await fetchShipmentAliquots(shipment.shipmentId));
    } catch (err) {
      setError('Could not verify the shipment aliquots and their Study attribution.');
    }
  };

  const columns = [
    { field: 'shipmentId', headerName: 'Shipment ID', flex: 1, minWidth: 230 },
    { field: 'recipientName', headerName: 'Recipient', flex: 1, minWidth: 160 },
    { field: 'recipientInstitution', headerName: 'Institution', flex: 1, minWidth: 180 },
    { field: 'shipOutDate', headerName: 'Ship Out', flex: 1, minWidth: 180,
      renderCell: params => params.value ? formatDateTimeInAppTimeZone(params.value) : 'N/A' },
    { field: 'receiveDate', headerName: 'Received', flex: 1, minWidth: 180,
      renderCell: params => params.value ? formatDateTimeInAppTimeZone(params.value) : 'Pending' },
    { field: 'actions', headerName: 'Aliquots', width: 130, sortable: false,
      renderCell: params => <Button onClick={() => show(params.row)}>View</Button> },
  ];

  return (
    <Box sx={{ minHeight: 500, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Typography color="error">{error}</Typography>}
      <Box sx={{ flex: 1, minHeight: 400 }}>
        <StyledDataGrid rows={shipments.map(shipment => ({ ...shipment, id: shipment.shipmentId }))}
          columns={columns} loading={loading} pagination paginationMode="server" rowCount={total}
          paginationModel={page} onPaginationModelChange={setPage} pageSizeOptions={[10, 25, 50]} />
      </Box>
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Shipment Aliquots</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Shipment: {selected?.shipmentId}</Typography>
          {aliquots.length ? aliquots.map(aliquot => <Typography key={aliquot.aliquotId}>{aliquot.aliquotId}</Typography>)
            : <Typography variant="body2">No verified aliquots found.</Typography>}
        </DialogContent>
        <DialogActions><Button onClick={() => setSelected(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShipmentHistory;
